import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useKitchenSocket from '../../hooks/useKitchenSocket';
import useKitchenOrders from '../../hooks/useKitchenOrders';
import useAudioAlert from '../../hooks/useAudioAlert';
import { formatTime } from '../../utils/formatDate';
import { NEW_ORDER_HIGHLIGHT_MS } from '../../utils/kitchenConfig';
import { toast } from '../../lib/toast';
import OrderQueue from '../../components/Kitchen/OrderQueue';
import StationSelector from '../../components/Kitchen/StationSelector';
import SoundToggle from '../../components/Kitchen/SoundToggle';
import './KitchenDisplay.css';

// Per-restaurant persistence of the cook's view preferences.
const readStored = (restaurantId, suffix, fallback) => {
  try {
    const raw = localStorage.getItem(`kds:${restaurantId}:${suffix}`);
    return raw != null ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
};
const writeStored = (restaurantId, suffix, value) => {
  try {
    localStorage.setItem(`kds:${restaurantId}:${suffix}`, JSON.stringify(value));
  } catch (_) {
    /* storage unavailable — non-fatal */
  }
};

const KitchenDisplay = () => {
  const { restaurantId } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'es' ? 'es-ES' : 'en-US';

  const [selectedStations, setSelectedStations] = useState(() => readStored(restaurantId, 'stations', []));
  const [viewMode, setViewMode] = useState(() => readStored(restaurantId, 'view', 'grid'));
  const [soundEnabled, setSoundEnabled] = useState(() => readStored(restaurantId, 'sound', true));
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastBumpedOrderId, setLastBumpedOrderId] = useState(null);
  const [newOrderIds, setNewOrderIds] = useState(() => new Set());
  const audioWarnedRef = useRef(false);

  // Persist view preferences whenever they change.
  useEffect(() => { writeStored(restaurantId, 'stations', selectedStations); }, [restaurantId, selectedStations]);
  useEffect(() => { writeStored(restaurantId, 'view', viewMode); }, [restaurantId, viewMode]);
  useEffect(() => { writeStored(restaurantId, 'sound', soundEnabled); }, [restaurantId, soundEnabled]);

  const {
    orders,
    stations,
    loading,
    error,
    refreshOrders,
    startItem,
    completeItem,
    resetItem,
    bumpOrder,
    recallOrder
  } = useKitchenOrders(restaurantId, selectedStations);

  const { playSound } = useAudioAlert({ enabled: soundEnabled, volume: 0.7 });

  // Briefly highlight an order that just arrived.
  const flagNewOrder = useCallback((orderId) => {
    if (orderId == null) return;
    setNewOrderIds((prev) => new Set(prev).add(orderId));
    setTimeout(() => {
      setNewOrderIds((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }, NEW_ORDER_HIGHLIGHT_MS);
  }, []);

  const { connected } = useKitchenSocket(restaurantId, selectedStations, {
    onNewOrder: (order) => {
      const played = playSound();
      if (!played && soundEnabled && !audioWarnedRef.current) {
        audioWarnedRef.current = true;
        toast(t('kitchen.audioBlocked'), { icon: '🔇' });
      }
      flagNewOrder(order && order.id);
      refreshOrders();
    },
    onOrderUpdate: refreshOrders,
    onItemUpdate: refreshOrders,
    onOrderBumped: refreshOrders,
    onOrderRecalled: refreshOrders,
    onReconnect: refreshOrders
  });

  // Safety-net poll in case the socket silently misses an event.
  useEffect(() => {
    const interval = setInterval(refreshOrders, 30000);
    return () => clearInterval(interval);
  }, [refreshOrders]);

  const handleBumpOrder = useCallback((orderId) => {
    setLastBumpedOrderId(orderId);
    bumpOrder(orderId);
  }, [bumpOrder]);

  const handleRecall = useCallback(() => {
    if (lastBumpedOrderId == null) return;
    recallOrder(lastBumpedOrderId);
    setLastBumpedOrderId(null);
  }, [lastBumpedOrderId, recallOrder]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'f' || e.key === 'F') toggleFullscreen();
      if (e.key === 'm' || e.key === 'M') setSoundEnabled((prev) => !prev);
      if (e.key === 'r' || e.key === 'R') refreshOrders();
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [toggleFullscreen, refreshOrders]);

  const renderContent = () => {
    if (loading && orders.length === 0) {
      return (
        <div className="kds-loading">
          <div className="kds-loading__spinner" />
          <p>{t('kitchen.loading')}</p>
        </div>
      );
    }
    if (error && orders.length === 0) {
      return (
        <div className="kds-error" role="alert">
          <div className="kds-error__icon" aria-hidden>⚠️</div>
          <h2 className="kds-error__title">{t('kitchen.errorTitle')}</h2>
          <p className="kds-error__text">{t('kitchen.errorText')}</p>
          <button type="button" className="kds-error__retry" onClick={refreshOrders}>
            {t('kitchen.retry')}
          </button>
        </div>
      );
    }
    if (orders.length === 0) {
      return (
        <div className="kds-empty">
          <div className="kds-empty__icon">✓</div>
          <h2 className="kds-empty__title">{t('kitchen.emptyTitle')}</h2>
          <p className="kds-empty__text">{t('kitchen.emptyText')}</p>
        </div>
      );
    }
    return (
      <OrderQueue
        orders={orders}
        viewMode={viewMode}
        newOrderIds={newOrderIds}
        onStartItem={startItem}
        onCompleteItem={completeItem}
        onResetItem={resetItem}
        onBumpOrder={handleBumpOrder}
      />
    );
  };

  return (
    <div className="kitchen-display">
      <header className="kds-header">
        <div className="kds-header__left">
          <h1 className="kds-header__title">{t('kitchen.title')}</h1>
          <div className="kds-header__connection">
            <span className={`kds-header__dot ${connected ? 'kds-header__dot--connected' : 'kds-header__dot--disconnected'}`} aria-hidden />
            <span>{connected ? t('kitchen.live') : t('kitchen.offline')}</span>
          </div>
        </div>
        <div className="kds-header__center">
          <StationSelector
            stations={stations}
            selected={selectedStations}
            onChange={setSelectedStations}
          />
        </div>
        <div className="kds-header__right">
          <SoundToggle enabled={soundEnabled} onChange={setSoundEnabled} />
          <button
            type="button"
            className="kds-header__btn"
            onClick={() => setViewMode(viewMode === 'grid' ? 'columns' : 'grid')}
            title={t('kitchen.toggleView')}
          >
            {viewMode === 'grid' ? '📋' : '📊'}
          </button>
          <button
            type="button"
            className="kds-header__btn"
            onClick={toggleFullscreen}
            title={t('kitchen.fullscreen')}
          >
            {isFullscreen ? '🗗' : '⛶'}
          </button>
          <button
            type="button"
            className="kds-header__btn kds-header__btn--refresh"
            onClick={refreshOrders}
            title={t('kitchen.refresh')}
          >
            🔄
          </button>
          <Link
            to={`/kitchen/${restaurantId}/settings`}
            className="kds-header__btn kds-header__btn--settings"
            title={t('kitchen.settings')}
          >
            ⚙️
          </Link>
        </div>
      </header>

      <main className="kds-content">
        {renderContent()}
      </main>

      <footer className="kds-footer">
        <div className="kds-footer__stats">
          <div className="kds-footer__stat">
            <span className="kds-footer__stat-label">{t('kitchen.pending')}</span>
            <span className="kds-footer__stat-value">
              {orders.filter((o) => o.status === 'pending').length}
            </span>
          </div>
          <div className="kds-footer__stat">
            <span className="kds-footer__stat-label">{t('kitchen.preparing')}</span>
            <span className="kds-footer__stat-value">
              {orders.filter((o) => o.status === 'preparing').length}
            </span>
          </div>
          <div className="kds-footer__stat">
            <span className="kds-footer__stat-label">{t('kitchen.ready')}</span>
            <span className="kds-footer__stat-value">
              {orders.filter((o) => o.status === 'ready').length}
            </span>
          </div>
          <div className="kds-footer__stat">
            <span className="kds-footer__stat-label">{t('kitchen.total')}</span>
            <span className="kds-footer__stat-value">{orders.length}</span>
          </div>
        </div>
        <div className="kds-footer__actions">
          <button
            type="button"
            className="kds-footer__recall"
            onClick={handleRecall}
            disabled={lastBumpedOrderId == null}
            title={t('kitchen.recallHint')}
          >
            ↩ {t('kitchen.recallOrder')}
          </button>
          <div className="kds-footer__time">
            {formatTime(new Date(), locale)}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default KitchenDisplay;
